// ============================================================================
// SECURITY FLAG SERVICE - Flag Management
// ============================================================================
// Core service for creating, querying, and resolving security flags.
// All detections create FLAGS - no auto-bans. Humans review and act.
// ============================================================================

import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  writeBatch,
  increment,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  SecurityFlag,
  SecurityFlagType,
  FlagSeverity,
  FlagStatus,
  FlagResolution,
  ActionTaken,
  CreateFlagRequest,
  FlagQueryOptions,
  UpdateFlagRequest,
  FlagEvidence,
  FlagListItem,
  SecurityDashboardStats,
  FLAG_THRESHOLDS,
} from '../../types/securityTypes';

// ============================================================================
// CONSTANTS
// ============================================================================

const COLLECTION_NAME = 'securityFlags';
const STATS_DOC = 'flagStats';
const MAX_FLAGS_PER_QUERY = 100;

// Severity order for sorting
const SEVERITY_ORDER: Record<FlagSeverity, number> = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
};

// ============================================================================
// SECURITY FLAG SERVICE CLASS
// ============================================================================

class SecurityFlagService {
  // ============================================================================
  // FLAG CREATION
  // ============================================================================

  /**
   * Create a new security flag
   * @param request - Flag creation request
   * @returns The created flag ID
   */
  async createFlag(request: CreateFlagRequest): Promise<string> {
    try {
      console.log(`🚨 [SecurityFlag] Creating ${request.type} flag for ${request.primaryUserId.substring(0, 8)}`);

      // Generate flag ID
      const flagId = this.generateFlagId(request.type);

      // Build flag document
      const flag: SecurityFlag = {
        id: flagId,
        type: request.type,
        severity: request.severity,
        confidence: request.confidence,
        status: 'new',
        flaggedUserIds: request.relatedUserId 
          ? [request.primaryUserId, request.relatedUserId]
          : [request.primaryUserId],
        primaryUserId: request.primaryUserId,
        relatedUserId: request.relatedUserId,
        evidence: request.evidence,
        createdAt: Timestamp.now(),
        createdBy: 'system',
      };

      // Save to Firestore
      const flagRef = doc(db, COLLECTION_NAME, flagId);
      await setDoc(flagRef, flag);

      // Update stats
      await this.incrementFlagStats(request.type, request.severity);

      console.log(`✅ [SecurityFlag] Created flag: ${flagId} (${request.severity} severity, ${request.confidence}% confidence)`);

      return flagId;

    } catch (error) {
      console.error('❌ [SecurityFlag] Error creating flag:', error);
      throw error;
    }
  }

  /**
   * Create flag only if confidence meets threshold
   * @returns Flag ID if created, null if threshold not met
   */
  async createFlagIfThresholdMet(
    request: CreateFlagRequest,
    threshold: number
  ): Promise<string | null> {
    if (request.confidence < threshold) {
      console.log(`ℹ️ [SecurityFlag] Skipping flag creation - confidence ${request.confidence}% < threshold ${threshold}%`);
      return null;
    }
    return this.createFlag(request);
  }

  /**
   * Check if a similar flag already exists (to avoid duplicates)
   */
  async hasExistingFlag(
    type: SecurityFlagType,
    primaryUserId: string,
    relatedUserId?: string,
    withinHours: number = 24
  ): Promise<boolean> {
    try {
      const cutoffTime = Timestamp.fromMillis(
        Date.now() - withinHours * 60 * 60 * 1000
      );

      const constraints: QueryConstraint[] = [
        where('type', '==', type),
        where('primaryUserId', '==', primaryUserId),
        where('createdAt', '>=', cutoffTime),
        where('status', 'in', ['new', 'reviewing']),
        limit(1),
      ];

      const q = query(collection(db, COLLECTION_NAME), ...constraints);
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return false;
      }

      // If related user specified, check if it matches
      if (relatedUserId) {
        const existingFlag = snapshot.docs[0].data() as SecurityFlag;
        return existingFlag.relatedUserId === relatedUserId;
      }

      return true;

    } catch (error) {
      console.error('❌ [SecurityFlag] Error checking existing flag:', error);
      return false; // Err on side of creating flag
    }
  }

  // ============================================================================
  // FLAG QUERIES
  // ============================================================================

  /**
   * Get a single flag by ID
   */
  async getFlag(flagId: string): Promise<SecurityFlag | null> {
    try {
      const flagRef = doc(db, COLLECTION_NAME, flagId);
      const snapshot = await getDoc(flagRef);

      if (!snapshot.exists()) {
        return null;
      }

      return snapshot.data() as SecurityFlag;

    } catch (error) {
      console.error('❌ [SecurityFlag] Error getting flag:', error);
      return null;
    }
  }

  /**
   * Get flags with optional filters
   */
  async getFlags(options: FlagQueryOptions = {}): Promise<SecurityFlag[]> {
    try {
      const constraints: QueryConstraint[] = [];

      // Add filters
      if (options.type) {
        constraints.push(where('type', '==', options.type));
      }
      if (options.severity) {
        constraints.push(where('severity', '==', options.severity));
      }
      if (options.status) {
        constraints.push(where('status', '==', options.status));
      }
      if (options.userId) {
        constraints.push(where('flaggedUserIds', 'array-contains', options.userId));
      }
      if (options.assignedTo) {
        constraints.push(where('assignedTo', '==', options.assignedTo));
      }

      // Add ordering
      const orderField = options.orderBy || 'createdAt';
      const orderDir = options.orderDirection || 'desc';
      constraints.push(orderBy(orderField, orderDir));

      // Add limit
      constraints.push(limit(options.limit || MAX_FLAGS_PER_QUERY));

      const q = query(collection(db, COLLECTION_NAME), ...constraints);
      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => doc.data() as SecurityFlag);

    } catch (error) {
      console.error('❌ [SecurityFlag] Error getting flags:', error);
      return [];
    }
  }

  /**
   * Get all flags for a specific player
   */
  async getPlayerFlags(userId: string): Promise<SecurityFlag[]> {
    return this.getFlags({ userId, orderBy: 'createdAt', orderDirection: 'desc' });
  }

  /**
   * Get flags by type
   */
  async getFlagsByType(type: SecurityFlagType, status?: FlagStatus): Promise<SecurityFlag[]> {
    const options: FlagQueryOptions = { type, orderBy: 'createdAt', orderDirection: 'desc' };
    if (status) {
      options.status = status;
    }
    return this.getFlags(options);
  }

  /**
   * Get all new/unreviewed flags (for admin dashboard)
   */
  async getNewFlags(limitCount: number = 50): Promise<SecurityFlag[]> {
    return this.getFlags({
      status: 'new',
      orderBy: 'createdAt',
      orderDirection: 'desc',
      limit: limitCount,
    });
  }

  /**
   * Get high priority flags (critical/high severity, new status)
   */
  async getHighPriorityFlags(): Promise<SecurityFlag[]> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('status', '==', 'new'),
        where('severity', 'in', ['critical', 'high']),
        orderBy('createdAt', 'desc'),
        limit(20)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => doc.data() as SecurityFlag);

    } catch (error) {
      console.error('❌ [SecurityFlag] Error getting high priority flags:', error);
      return [];
    }
  }

  /**
   * Get flags for admin list view (with usernames)
   */
  async getFlagsForList(options: FlagQueryOptions = {}): Promise<FlagListItem[]> {
    const flags = await this.getFlags(options);
    
    // In a real implementation, you'd batch fetch usernames
    // For now, we'll return with IDs only
    return flags.map(flag => ({
      id: flag.id,
      type: flag.type,
      severity: flag.severity,
      confidence: flag.confidence,
      status: flag.status,
      primaryUserId: flag.primaryUserId,
      relatedUserId: flag.relatedUserId,
      createdAt: flag.createdAt,
      evidenceSummary: this.generateEvidenceSummary(flag.evidence),
    }));
  }

  // ============================================================================
  // FLAG UPDATES
  // ============================================================================

  /**
   * Update a flag
   */
  async updateFlag(flagId: string, updates: UpdateFlagRequest): Promise<void> {
    try {
      const flagRef = doc(db, COLLECTION_NAME, flagId);

      const updateData: any = { ...updates };

      // Add assignment timestamp if assigning
      if (updates.assignedTo) {
        updateData.assignedAt = Timestamp.now();
      }

      // Add resolution timestamp if resolving
      if (updates.resolution) {
        updateData.resolvedAt = Timestamp.now();
      }

      await updateDoc(flagRef, updateData);

      console.log(`✅ [SecurityFlag] Updated flag ${flagId}`);

    } catch (error) {
      console.error('❌ [SecurityFlag] Error updating flag:', error);
      throw error;
    }
  }

  /**
   * Assign flag to an admin
   */
  async assignFlag(flagId: string, adminUserId: string): Promise<void> {
    await this.updateFlag(flagId, {
      status: 'reviewing',
      assignedTo: adminUserId,
    });
  }

  /**
   * Resolve a flag
   */
  async resolveFlag(
    flagId: string,
    adminUserId: string,
    resolution: FlagResolution,
    actionTaken: ActionTaken = 'none',
    notes?: string
  ): Promise<void> {
    try {
      const flagRef = doc(db, COLLECTION_NAME, flagId);

      await updateDoc(flagRef, {
        status: 'resolved',
        resolution,
        resolutionNotes: notes || null,
        actionTaken,
        resolvedAt: Timestamp.now(),
        resolvedBy: adminUserId,
      });

      // Decrement active flag stats
      const flag = await this.getFlag(flagId);
      if (flag) {
        await this.decrementFlagStats(flag.type, flag.severity);
      }

      console.log(`✅ [SecurityFlag] Resolved flag ${flagId}: ${resolution}, action: ${actionTaken}`);

    } catch (error) {
      console.error('❌ [SecurityFlag] Error resolving flag:', error);
      throw error;
    }
  }

  /**
   * Dismiss a flag (false positive without full review)
   */
  async dismissFlag(flagId: string, adminUserId: string, reason?: string): Promise<void> {
    await this.resolveFlag(flagId, adminUserId, 'false_positive', 'none', reason);
  }

  // ============================================================================
  // STATISTICS
  // ============================================================================

  /**
   * Get dashboard statistics
   */
  async getDashboardStats(): Promise<SecurityDashboardStats> {
    try {
      // Get flag counts by status
      const [newFlags, reviewingFlags] = await Promise.all([
        this.countFlagsByStatus('new'),
        this.countFlagsByStatus('reviewing'),
      ]);

      // Get today's resolved
      const todayStart = Timestamp.fromMillis(
        new Date().setHours(0, 0, 0, 0)
      );
      const resolvedTodayQuery = query(
        collection(db, COLLECTION_NAME),
        where('status', '==', 'resolved'),
        where('resolvedAt', '>=', todayStart)
      );
      const resolvedTodaySnap = await getDocs(resolvedTodayQuery);

      // Get this week's resolved
      const weekStart = Timestamp.fromMillis(
        Date.now() - 7 * 24 * 60 * 60 * 1000
      );
      const resolvedWeekQuery = query(
        collection(db, COLLECTION_NAME),
        where('status', '==', 'resolved'),
        where('resolvedAt', '>=', weekStart)
      );
      const resolvedWeekSnap = await getDocs(resolvedWeekQuery);

      // Get counts by type (new/reviewing only)
      const [collusionFlags, multiAccountFlags, botFlags, chipDumpFlags] = await Promise.all([
        this.countActiveFlagsByType('collusion'),
        this.countActiveFlagsByType('multi_account'),
        this.countActiveFlagsByType('bot'),
        this.countActiveFlagsByType('chip_dumping'),
      ]);

      // Get counts by severity (new/reviewing only)
      const [criticalFlags, highFlags, mediumFlags, lowFlags] = await Promise.all([
        this.countActiveFlagsBySeverity('critical'),
        this.countActiveFlagsBySeverity('high'),
        this.countActiveFlagsBySeverity('medium'),
        this.countActiveFlagsBySeverity('low'),
      ]);

      // Get time-based counts
      const day24h = Timestamp.fromMillis(Date.now() - 24 * 60 * 60 * 1000);
      const day7d = Timestamp.fromMillis(Date.now() - 7 * 24 * 60 * 60 * 1000);
      const day30d = Timestamp.fromMillis(Date.now() - 30 * 24 * 60 * 60 * 1000);

      const [flags24h, flags7d, flags30d] = await Promise.all([
        this.countFlagsSince(day24h),
        this.countFlagsSince(day7d),
        this.countFlagsSince(day30d),
      ]);

      // Get unique flagged players count
      const uniquePlayers = await this.getUniqueFlaggedPlayerCount();

      // High risk players (3+ active flags or any critical flag)
      const highRiskPlayers = await this.getHighRiskPlayerCount();

      return {
        newFlags,
        reviewingFlags,
        resolvedToday: resolvedTodaySnap.size,
        resolvedThisWeek: resolvedWeekSnap.size,
        collusionFlags,
        multiAccountFlags,
        botFlags,
        chipDumpFlags,
        criticalFlags,
        highFlags,
        mediumFlags,
        lowFlags,
        flagsLast24h: flags24h,
        flagsLast7d: flags7d,
        flagsLast30d: flags30d,
        flaggedPlayers: uniquePlayers,
        highRiskPlayers,
      };

    } catch (error) {
      console.error('❌ [SecurityFlag] Error getting dashboard stats:', error);
      return {
        newFlags: 0,
        reviewingFlags: 0,
        resolvedToday: 0,
        resolvedThisWeek: 0,
        collusionFlags: 0,
        multiAccountFlags: 0,
        botFlags: 0,
        chipDumpFlags: 0,
        criticalFlags: 0,
        highFlags: 0,
        mediumFlags: 0,
        lowFlags: 0,
        flagsLast24h: 0,
        flagsLast7d: 0,
        flagsLast30d: 0,
        flaggedPlayers: 0,
        highRiskPlayers: 0,
      };
    }
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  /**
   * Generate unique flag ID
   */
  private generateFlagId(type: SecurityFlagType): string {
    const prefix = type.substring(0, 3).toUpperCase();
    const timestamp = Date.now().toString(36).toUpperCase();
    const random = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `FL-${prefix}-${timestamp}-${random}`;
  }

  /**
   * Generate human-readable evidence summary
   */
  private generateEvidenceSummary(evidence: FlagEvidence): string {
    if (evidence.summary) {
      return evidence.summary;
    }

    const signals = evidence.signals.slice(0, 3);
    return signals.length > 0 
      ? signals.join(', ') 
      : 'No specific signals';
  }

  /**
   * Increment flag statistics
   */
  private async incrementFlagStats(type: SecurityFlagType, severity: FlagSeverity): Promise<void> {
    try {
      const statsRef = doc(db, 'platformStats', STATS_DOC);
      const statsDoc = await getDoc(statsRef);

      if (!statsDoc.exists()) {
        await setDoc(statsRef, {
          totalFlags: 1,
          activeFlags: 1,
          [`type_${type}`]: 1,
          [`severity_${severity}`]: 1,
          lastUpdated: Timestamp.now(),
        });
      } else {
        await updateDoc(statsRef, {
          totalFlags: increment(1),
          activeFlags: increment(1),
          [`type_${type}`]: increment(1),
          [`severity_${severity}`]: increment(1),
          lastUpdated: Timestamp.now(),
        });
      }
    } catch (error) {
      console.error('Error updating flag stats:', error);
    }
  }

  /**
   * Decrement active flag count
   */
  private async decrementFlagStats(type: SecurityFlagType, severity: FlagSeverity): Promise<void> {
    try {
      const statsRef = doc(db, 'platformStats', STATS_DOC);
      await updateDoc(statsRef, {
        activeFlags: increment(-1),
        lastUpdated: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error updating flag stats:', error);
    }
  }

  /**
   * Count flags by status
   */
  private async countFlagsByStatus(status: FlagStatus): Promise<number> {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('status', '==', status)
    );
    const snapshot = await getDocs(q);
    return snapshot.size;
  }

  /**
   * Count active flags by type
   */
  private async countActiveFlagsByType(type: SecurityFlagType): Promise<number> {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('type', '==', type),
      where('status', 'in', ['new', 'reviewing'])
    );
    const snapshot = await getDocs(q);
    return snapshot.size;
  }

  /**
   * Count active flags by severity
   */
  private async countActiveFlagsBySeverity(severity: FlagSeverity): Promise<number> {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('severity', '==', severity),
      where('status', 'in', ['new', 'reviewing'])
    );
    const snapshot = await getDocs(q);
    return snapshot.size;
  }

  /**
   * Count flags created since a timestamp
   */
  private async countFlagsSince(since: Timestamp): Promise<number> {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('createdAt', '>=', since)
    );
    const snapshot = await getDocs(q);
    return snapshot.size;
  }

  /**
   * Get count of unique flagged players
   */
  private async getUniqueFlaggedPlayerCount(): Promise<number> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('status', 'in', ['new', 'reviewing'])
      );
      const snapshot = await getDocs(q);
      
      const playerIds = new Set<string>();
      snapshot.docs.forEach(doc => {
        const flag = doc.data() as SecurityFlag;
        flag.flaggedUserIds.forEach(id => playerIds.add(id));
      });
      
      return playerIds.size;
    } catch (error) {
      console.error('Error counting unique flagged players:', error);
      return 0;
    }
  }

  /**
   * Get count of high-risk players
   */
  private async getHighRiskPlayerCount(): Promise<number> {
    try {
      // Players with critical severity flags
      const criticalQuery = query(
        collection(db, COLLECTION_NAME),
        where('severity', '==', 'critical'),
        where('status', 'in', ['new', 'reviewing'])
      );
      const criticalSnapshot = await getDocs(criticalQuery);
      
      const highRiskPlayers = new Set<string>();
      criticalSnapshot.docs.forEach(doc => {
        const flag = doc.data() as SecurityFlag;
        highRiskPlayers.add(flag.primaryUserId);
      });
      
      return highRiskPlayers.size;
    } catch (error) {
      console.error('Error counting high-risk players:', error);
      return 0;
    }
  }

  /**
   * Determine severity based on confidence score
   */
  determineSeverity(confidence: number, type: SecurityFlagType): FlagSeverity {
    if (confidence >= 90) return 'critical';
    if (confidence >= 75) return 'high';
    if (confidence >= 50) return 'medium';
    return 'low';
  }

  /**
   * Get severity color for UI
   */
  getSeverityColor(severity: FlagSeverity): string {
    switch (severity) {
      case 'critical': return 'red';
      case 'high': return 'orange';
      case 'medium': return 'yellow';
      case 'low': return 'gray';
      default: return 'gray';
    }
  }

  /**
   * Get type display name
   */
  getTypeDisplayName(type: SecurityFlagType): string {
    switch (type) {
      case 'collusion': return 'Collusion';
      case 'multi_account': return 'Multi-Account';
      case 'bot': return 'Bot Suspected';
      case 'chip_dumping': return 'Chip Dumping';
      case 'same_ip_table': return 'Same IP at Table';
      case 'suspicious_timing': return 'Suspicious Timing';
      case 'unusual_pattern': return 'Unusual Pattern';
      default: return type;
    }
  }

  // ============================================================================
  // CONVENIENCE METHODS (for component compatibility)
  // ============================================================================

  /**
   * Get all active flags (new + reviewing status)
   */
  async getActiveFlags(limitCount: number = 100): Promise<SecurityFlag[]> {
    try {
      const newFlags = await this.getFlags({ status: 'new', limit: limitCount });
      const reviewingFlags = await this.getFlags({ status: 'reviewing', limit: limitCount });
      return [...newFlags, ...reviewingFlags].slice(0, limitCount);
    } catch (error) {
      console.error('❌ [SecurityFlag] Error getting active flags:', error);
      return [];
    }
  }

  /**
   * Get all flags with optional limit
   */
  async getAllFlags(limitCount: number = 100): Promise<SecurityFlag[]> {
    try {
      return await this.getFlags({ limit: limitCount });
    } catch (error) {
      console.error('❌ [SecurityFlag] Error getting all flags:', error);
      return [];
    }
  }

  /**
   * Get recent flags within a time window
   */
  async getRecentFlags(limitCount: number = 10, timeWindowMs: number = 24 * 60 * 60 * 1000): Promise<SecurityFlag[]> {
    try {
      const cutoff = Timestamp.fromMillis(Date.now() - timeWindowMs);
      const q = query(
        collection(db, COLLECTION_NAME),
        where('createdAt', '>=', cutoff),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SecurityFlag));
    } catch (error) {
      console.error('❌ [SecurityFlag] Error getting recent flags:', error);
      return [];
    }
  }

  /**
   * Get flags by status
   */
  async getFlagsByStatus(status: FlagStatus, limitCount: number = 100): Promise<SecurityFlag[]> {
    try {
      return await this.getFlags({ status, limit: limitCount });
    } catch (error) {
      console.error('❌ [SecurityFlag] Error getting flags by status:', error);
      return [];
    }
  }
}

// ============================================================================
// SINGLETON EXPORT
// ============================================================================

const securityFlagService = new SecurityFlagService();
export { securityFlagService };
export default securityFlagService;